import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, Eye, Trash2, Send } from "lucide-react";
import StatusPill from "@/components/StatusPill";
import { TESTIDS } from "@/constants/testIds";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export default function ThreadsTab({ projectId, onRefresh }) {
  const [threads, setThreads] = useState([]);
  const [count, setCount] = useState(1);
  const [serviceFilter, setServiceFilter] = useState("");
  const [intentFilter, setIntentFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [selection, setSelection] = useState(new Set());
  const [preview, setPreview] = useState(null);

  async function load() {
    try { const { data } = await api.get("/threads", { params: { project_id: projectId } }); setThreads(data); }
    catch { toast.error("Failed to load threads"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  async function generate() {
    setBusy(true);
    try {
      const params = { project_id: projectId, count };
      if (serviceFilter) params.service_filter = serviceFilter;
      if (intentFilter) params.intent_filter = intentFilter;
      const { data } = await api.post("/threads/generate", null, { params });
      toast.success(`Generated ${data.count} thread${data.count !== 1 ? "s" : ""}${data.errors?.length ? ` (${data.errors.length} errors)` : ""}`);
      if (data.errors?.length) console.warn("Generation errors:", data.errors);
      load(); onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(false); }
  }

  async function showPreview(t) {
    try { const { data } = await api.get(`/threads/${t.id}`); setPreview(data); }
    catch { toast.error("Failed to load preview"); }
  }

  async function del(id) {
    await api.delete(`/threads/${id}`);
    setSelection((s) => { s.delete(id); return new Set(s); });
    load(); onRefresh?.();
  }

  async function scheduleSelected() {
    const ids = Array.from(selection);
    if (!ids.length) return toast.error("Select at least one thread");
    try {
      const { data } = await api.post("/publishing/schedule", { thread_ids: ids });
      toast.success(`Scheduled ${data.count} thread${data.count !== 1 ? "s" : ""} (${data.cadence_per_day}/day cadence)`);
      setSelection(new Set()); load(); onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Schedule failed"); }
  }

  function toggleSel(id) {
    setSelection((s) => { const ns = new Set(s); if (ns.has(id)) ns.delete(id); else ns.add(id); return ns; });
  }

  return (
    <div>
      <div className="border border-border p-4 mb-4">
        <h3 className="text-xs txt-mono uppercase tracking-widest text-muted-foreground mb-3">Generate threads</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[10px] txt-mono uppercase text-muted-foreground mb-1">Count</div>
            <input data-testid={TESTIDS.genCountInput} type="number" min={1} max={20} value={count} onChange={(e) => setCount(Number(e.target.value))}
              className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm w-20 txt-mono" />
          </div>
          <div>
            <div className="text-[10px] txt-mono uppercase text-muted-foreground mb-1">Service filter</div>
            <select data-testid={TESTIDS.genServiceSelect} value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}
              className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm txt-mono">
              <option value="">Any</option>
              <option value="water-damage">Water Damage</option>
              <option value="plumbing">Plumbing</option>
              <option value="hvac">HVAC</option>
              <option value="mold">Mold</option>
              <option value="roofing">Roofing</option>
              <option value="electrical">Electrical</option>
              <option value="pest-control">Pest Control</option>
              <option value="foundation">Foundation</option>
              <option value="renovation">Renovation</option>
              <option value="tree-removal">Tree Removal</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] txt-mono uppercase text-muted-foreground mb-1">Intent (force)</div>
            <select data-testid={TESTIDS.genIntentSelect} value={intentFilter} onChange={(e) => setIntentFilter(e.target.value)}
              className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm txt-mono">
              <option value="">Auto</option>
              <option value="urgent">Urgent</option>
              <option value="cost">Cost</option>
              <option value="diagnostic">Diagnostic</option>
              <option value="comparison">Comparison</option>
              <option value="insurance">Insurance</option>
              <option value="local">Local</option>
              <option value="informational">Informational</option>
              <option value="diy">DIY</option>
            </select>
          </div>
          <button
            data-testid={TESTIDS.genGoBtn}
            onClick={generate} disabled={busy}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-4 py-2 text-sm font-medium rounded-sm transition-colors"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {busy ? "Generating…" : "Generate"}
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Each thread costs ~$0.01–0.05 in GPT-5.4 tokens. Pulls from keywords with status `imported` or `clustered`.</p>
      </div>

      <div className="flex items-center justify-between mb-2">
        <div className="text-xs text-muted-foreground">{threads.length} thread{threads.length !== 1 ? "s" : ""} · {selection.size} selected</div>
        <button
          data-testid={TESTIDS.scheduleBatchBtn}
          onClick={scheduleSelected} disabled={!selection.size}
          className="inline-flex items-center gap-2 text-xs px-3 py-2 border border-border hover:border-primary disabled:opacity-40 rounded-sm transition-colors"
        >
          <Send className="w-3 h-3" /> Schedule selected ({selection.size})
        </button>
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs" data-testid={TESTIDS.threadsTable}>
          <thead>
            <tr className="bg-secondary border-b border-border text-left">
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Title</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Service</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Intent</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Geo</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Status</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">WP</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {threads.map((t) => (
              <tr key={t.id} className="border-b border-border last:border-b-0 hover:bg-secondary/40">
                <td className="px-3 py-2"><input type="checkbox" checked={selection.has(t.id)} onChange={() => toggleSel(t.id)} className="accent-primary" /></td>
                <td className="px-3 py-2 max-w-md">
                  <div className="truncate">{t.title}</div>
                  <div className="text-[10px] txt-mono text-muted-foreground truncate">{t.canonical_path}</div>
                </td>
                <td className="px-3 py-2 txt-mono">{t.service}</td>
                <td className="px-3 py-2 txt-mono">{t.intent}</td>
                <td className="px-3 py-2 txt-mono">{[t.city, (t.state || "").toUpperCase()].filter(Boolean).join(", ") || "—"}</td>
                <td className="px-3 py-2"><StatusPill status={t.status} /></td>
                <td className="px-3 py-2 txt-mono">{t.wp_post_id ? `#${t.wp_post_id}` : "—"}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button data-testid={TESTIDS.threadPreviewBtn} onClick={() => showPreview(t)} className="text-muted-foreground hover:text-primary mr-2"><Eye className="w-3 h-3 inline" /></button>
                  <button data-testid={TESTIDS.threadDeleteBtn} onClick={() => del(t.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-3 h-3 inline" /></button>
                </td>
              </tr>
            ))}
            {threads.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground text-xs">No threads yet. Import keywords first then click Generate.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">{preview?.title}</DialogTitle>
            <DialogDescription className="txt-mono text-xs">
              {preview?.canonical_path} · {preview?.intent} · template {preview?.template_variant}
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div>
              <div className="grid grid-cols-2 gap-4 text-xs mb-4">
                <div><div className="text-muted-foreground text-[10px] uppercase">Meta title</div><div className="txt-mono">{preview.meta_title}</div></div>
                <div><div className="text-muted-foreground text-[10px] uppercase">Meta description</div><div className="txt-mono text-muted-foreground">{preview.meta_description}</div></div>
              </div>
              <div className="text-[10px] txt-mono uppercase text-muted-foreground mb-2">Rendered HTML preview</div>
              <div className="border border-border p-4 bg-white text-black text-sm" style={{ colorScheme: "light" }} dangerouslySetInnerHTML={{ __html: preview.html_preview || "" }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
